import { hasMessage } from '@cloudsign/shared';

/** The small part of JSON Schema (from z.toJSONSchema) that the settings form understands. */
export interface JsonNode {
  type?: string | string[];
  properties?: Record<string, JsonNode>;
  enum?: unknown[];
  const?: unknown;
  format?: string;
  pattern?: string;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  default?: unknown;
  anyOf?: JsonNode[];
  oneOf?: JsonNode[];
}

export type FieldKind =
  | { kind: 'object'; properties: [string, JsonNode][] }
  | { kind: 'boolean' }
  | { kind: 'number'; integer: boolean; min?: number; max?: number }
  | { kind: 'select'; options: string[] }
  | { kind: 'colour' }
  | { kind: 'email' }
  | { kind: 'text'; maxLength?: number }
  | { kind: 'hidden' };

const COLOUR_PATTERN = '#[0-9a-fA-F]{6}';

function typeOf(node: JsonNode): string | undefined {
  return Array.isArray(node.type) ? node.type.find((x) => x !== 'null') : node.type;
}

/** Work out what control a schema node needs. Unknown or internal-looking nodes are hidden. */
export function classify(node: JsonNode): FieldKind {
  const variants = node.anyOf ?? node.oneOf;
  if (variants) {
    const real = variants.filter((v) => typeOf(v) !== 'null');
    if (real.some((v) => v.format === 'uuid')) return { kind: 'hidden' };
    if (real.length > 0 && real.every((v) => typeOf(v) === 'string')) {
      return real.some((v) => v.format === 'email') ? { kind: 'email' } : { kind: 'text' };
    }
    return real.length === 1 && real[0] ? classify(real[0]) : { kind: 'hidden' };
  }
  const type = typeOf(node);
  if (node.format === 'uuid') return { kind: 'hidden' };
  if (type === 'object' && node.properties) return { kind: 'object', properties: Object.entries(node.properties) };
  if (type === 'boolean') return { kind: 'boolean' };
  if (type === 'integer' || type === 'number') {
    return {
      kind: 'number',
      integer: type === 'integer',
      ...(node.minimum !== undefined ? { min: node.minimum } : {}),
      ...(node.maximum !== undefined ? { max: node.maximum } : {}),
    };
  }
  if (type === 'string') {
    if (node.enum) return { kind: 'select', options: node.enum.map(String) };
    if (node.pattern?.includes(COLOUR_PATTERN)) return { kind: 'colour' };
    if (node.format === 'email') return { kind: 'email' };
    return { kind: 'text', ...(node.maxLength !== undefined ? { maxLength: node.maxLength } : {}) };
  }
  return { kind: 'hidden' };
}

export function getAt(values: unknown, path: string[]): unknown {
  let cur: unknown = values;
  for (const k of path) {
    if (cur && typeof cur === 'object') cur = (cur as Record<string, unknown>)[k];
    else return undefined;
  }
  return cur;
}

export function setAt<T>(values: T, path: string[], value: unknown): T {
  const [head, ...rest] = path;
  if (head === undefined) return value as T;
  const obj = (values && typeof values === 'object' ? values : {}) as Record<string, unknown>;
  return { ...obj, [head]: setAt(obj[head], rest, value) } as T;
}

function isPlain(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** Only the changed values, as a nested partial object (this is what PATCH sends). */
export function diffValues(before: unknown, after: unknown): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!isPlain(after)) return out;
  for (const [k, v] of Object.entries(after)) {
    const b = isPlain(before) ? before[k] : undefined;
    if (isPlain(v)) {
      const d = diffValues(b, v);
      if (Object.keys(d).length > 0) out[k] = d;
    } else if (JSON.stringify(v) !== JSON.stringify(b)) {
      out[k] = v;
    }
  }
  return out;
}

/** Turn `passwordMinLength` into "Password min length" when no label exists for a setting. */
export function humanise(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function labelKeyFor(moduleKey: string, path: string[]): string {
  return `settings.${moduleKey}.${path.join('.')}`;
}

export function hasLabel(moduleKey: string, path: string[]): boolean {
  return hasMessage(labelKeyFor(moduleKey, path));
}
